import { inject, Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';
import * as Sentry from '@sentry/angular';

import { CORE_CONFIG } from '../config/core-config';
import { EncryptionService } from '../encryption/encryption.service';
import { getParsedValue } from './parsed-value';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private encryptionService = inject(EncryptionService);
  private config = inject(CORE_CONFIG);

  storagePrefix: string = this.config.storagePrefix || '';
  encrypted: boolean = this.config.storageEncryption || false;

  async set<T>(key: string, value: T): Promise<void> {
    const stringValue = JSON.stringify(value);
    const storedValue = this.encrypted ? this.encryptionService.encrypt(stringValue) : stringValue;
    await Preferences.set({ key: this.storagePrefix + key, value: storedValue });
  }

  async get<T>(key: string): Promise<T | null> {
    const result = await Preferences.get({ key: this.storagePrefix + key });
    if (!result.value) {
      return null;
    }
    try {
      if (!this.encrypted) {
        return getParsedValue(result.value);
      }
      const decryptedValue = this.encryptionService.decrypt(result.value);
      if (!decryptedValue) {
        // CryptoJS doesn't always throw on foreign input: decrypting a value
        // written in another format can yield '' instead. Same poison, same cure.
        throw new Error(`empty decryption output for non-empty stored value "${key}"`);
      }
      return getParsedValue(decryptedValue as string);
    } catch {
      // A value CryptoJS can't decrypt ("Malformed UTF-8 data") poisons every
      // read of this key — seen when Google Auto Backup restores preferences
      // written by an old app version onto a fresh install, which broke the
      // very first boot navigation (white screen). Discard it and behave as
      // if the key was never set.
      Sentry.captureMessage(`storage: discarding undecryptable value for "${key}"`, 'warning');
      await this.remove(key);
      return null;
    }
  }

  async clear(): Promise<void> {
    await Preferences.clear();
  }

  async remove(key: string): Promise<void> {
    await Preferences.remove({ key: this.storagePrefix + key });
  }

  /**
   * Keys (without the storage prefix, ready for get/remove) that start with `prefix`.
   */
  async keysWithPrefix(prefix: string): Promise<string[]> {
    const { keys } = await Preferences.keys();
    const rawPrefix = this.storagePrefix + prefix;
    return keys.filter((k) => k.startsWith(rawPrefix)).map((k) => k.slice(this.storagePrefix.length));
  }

  /**
   * Remove all Capacitor Preferences keys whose raw key starts with the given prefix.
   * Used to wipe legacy on-device caches once the server takes over (e.g. routes_2.0_*).
   */
  async removeKeysWithPrefix(rawPrefix: string): Promise<void> {
    const { keys } = await Preferences.keys();
    const matching = keys.filter((k) => k.startsWith(rawPrefix));
    await Promise.all(matching.map((key) => Preferences.remove({ key })));
  }
}
